import React, { useEffect, useRef, useState } from "react";
import { APIProvider, Map, Marker, useMap } from "@vis.gl/react-google-maps";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, MapPin, Move } from "lucide-react";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SYDNEY = { lat: -33.8688, lng: 151.2093 };
const field = "mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0B192C]";
const lbl = "text-xs font-semibold uppercase tracking-wider text-slate-500";

export const composeAddress = (v) =>
  [v.street, [v.suburb, v.state, v.postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");

const newToken = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()));

function SearchBox({ onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const token = useRef(newToken());
  const skip = useRef(false);

  useEffect(() => {
    if (skip.current) { skip.current = false; return undefined; }
    if (q.trim().length < 3) { setItems([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/places/autocomplete", { params: { q, session: token.current } });
        setItems(data);
      } catch { setItems([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pick = async (s) => {
    setBusy(true); setItems([]);
    try {
      const { data } = await api.get(`/places/details/${s.place_id}`, { params: { session: token.current } });
      token.current = newToken();
      skip.current = true;
      setQ(data.formatted || `${s.main} ${s.secondary}`);
      const { formatted, ...fields } = data;
      onPick(fields);
      toast.success("Address filled in");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Couldn't load that address"); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-[1.35rem] -translate-y-1/2 text-slate-400" />
      <input data-testid="address-search-input" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Start typing the address…" className={`${field} pl-9`} autoComplete="off" />
      {busy && <Loader2 size={16} className="absolute right-3 top-[1.35rem] -translate-y-1/2 animate-spin text-slate-400" />}
      {items.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="address-suggestions">
          {items.map((s, i) => (
            <li key={i}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)} data-testid={`address-suggestion-${i}`}
                className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50">
                <MapPin size={15} className="mt-0.5 shrink-0 text-[#FF6500]" />
                <span><span className="font-semibold text-[#0B192C]">{s.main}</span>{" "}
                  <span className="text-slate-500">{s.secondary}</span></span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Recenter({ pos }) {
  const map = useMap();
  useEffect(() => { if (map && pos) { map.panTo(pos); map.setZoom(18); } }, [map, pos?.lat, pos?.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function PinMap({ value, onMove }) {
  const pos = value.latitude != null && value.latitude !== "" ? { lat: +value.latitude, lng: +value.longitude } : null;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200" data-testid="address-pin-map">
      <Map style={{ height: 200 }} defaultCenter={pos || SYDNEY} defaultZoom={pos ? 18 : 10} gestureHandling="greedy"
        disableDefaultUI zoomControl mapTypeControl={false} streetViewControl={false}
        onClick={(e) => e.detail.latLng && onMove(e.detail.latLng.lat, e.detail.latLng.lng)}>
        {pos && <Marker position={pos} draggable onDragEnd={(e) => onMove(e.latLng.lat(), e.latLng.lng())} />}
        <Recenter pos={pos} />
      </Map>
      <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 text-xs text-slate-500">
        <Move size={12} /> {pos ? `Drag the pin to the exact delivery door · ${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}` : "Search an address to drop the pin"}
      </div>
    </div>
  );
}

export function AddressPicker({ value, onChange }) {
  const [gFailed, setGFailed] = useState(false);
  useEffect(() => {
    window.gm_authFailure = () => setGFailed(true);
    return () => { window.gm_authFailure = undefined; };
  }, []);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const parts = (
    <div className="grid grid-cols-6 gap-3">
      <div className="col-span-6"><label className={lbl}>Street</label><input data-testid="address-street-input" value={value.street || ""} onChange={(e) => set("street", e.target.value)} className={field} /></div>
      <div className="col-span-6 sm:col-span-3"><label className={lbl}>Suburb *</label><input data-testid="address-suburb-input" value={value.suburb || ""} onChange={(e) => set("suburb", e.target.value)} className={field} /></div>
      <div className="col-span-3 sm:col-span-1"><label className={lbl}>State</label><input data-testid="address-state-input" value={value.state || ""} onChange={(e) => set("state", e.target.value.toUpperCase())} className={field} /></div>
      <div className="col-span-3 sm:col-span-2"><label className={lbl}>Postcode</label><input data-testid="address-postcode-input" value={value.postcode || ""} onChange={(e) => set("postcode", e.target.value)} className={field} /></div>
    </div>
  );
  const notice = gFailed && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="address-google-unavailable">The map preview isn't available on this web address. Address search still works.</p>;
  if (!KEY) return parts;
  return (
    <APIProvider apiKey={KEY} region="AU">
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3" data-testid="address-picker">
        {notice}
        <div><label className={lbl}>Find Address</label><SearchBox onPick={(p) => onChange({ ...value, ...p })} /></div>
        {parts}
        <div className={gFailed ? "hidden" : ""}><PinMap value={value} onMove={(lat, lng) => onChange({ ...value, latitude: +lat.toFixed(6), longitude: +lng.toFixed(6) })} /></div>
      </div>
    </APIProvider>
  );
}
